import { useState } from "react";
import { Divider, Icon } from "semantic-ui-react";

import { FilePreview, type PreviewFile, useCan } from "components";
import "components/AttachmentPreview/AttachmentPreview.scss";
import { isImage, isVideo } from "components/FilePreview/utils";
import { ACL_ACTIVITIES, ACL_RESOURCES } from "models";
import { getGcsDownloadURL, getNameFromURL } from "utils/common";

interface PreviewAttachment extends PreviewFile {
  question_result_id?: number;
  visible_to_customer?: boolean;
}

type AttachmentPreviewProps = {
  canDelete?: boolean;
  isFilesVisible?: boolean;
  canPreviewFiles?: boolean;
  attachments?: PreviewAttachment[];
  onDeleteRequest?: (data: PreviewAttachment, index: number) => void;
  onToggleVisibility?: (visible_to_customer: boolean, intervention_attachment_id?: number) => void;
};

const FILENAME_MAX_LENGTH = 22;

const shortenFileName = (text: string, delimiter: number) => (text.length > delimiter ? `...${text.slice(-delimiter)}` : text);

const renderPreview = (attachment: PreviewAttachment, isFilesVisible: boolean) => {
  if (isImage("", attachment.url)) return <img alt="Attachment preview" src={attachment.url} />;

  if (isVideo("", attachment.url))
    return (
      <video controls key={attachment.url}>
        <source src={attachment.url} type="video/mp4" />
        <source src={attachment.url} type="video/quicktime" />
      </video>
    );

  if (isFilesVisible)
    return (
      <div className="media-other">
        <Icon size="huge" className="file" />
        <p>{shortenFileName(attachment.name || getNameFromURL(attachment.url), FILENAME_MAX_LENGTH)}</p>
      </div>
    );

  return null;
};

export const AttachmentsPreview = ({
  attachments,
  onDeleteRequest,
  onToggleVisibility,
  canDelete,
  canPreviewFiles = true,
  isFilesVisible = true
}: AttachmentPreviewProps) => {
  const [shownPreviewIndex, setShownPreviewIndex] = useState<number | null>(null);
  const canUpdateIntervention = useCan(ACL_ACTIVITIES.Update, ACL_RESOURCES.Interventions);

  if (!attachments?.length) return null;

  const handleDeleteAttachment = (e: React.MouseEvent, attachment: PreviewAttachment, index: number) => {
    e.stopPropagation();
    if (onDeleteRequest) onDeleteRequest(attachment, index);
    setShownPreviewIndex(null);
  };

  const handleUpdateVisibility = (e: React.SyntheticEvent, visible_to_customer: boolean, intervention_attachment_id?: number) => {
    e.stopPropagation();
    onToggleVisibility?.(visible_to_customer, intervention_attachment_id);
  };

  return (
    <div className="AttachmentPreview-Container">
      {attachments.map((attachment, index) => {
        return (
          <div key={attachment.url} className="media-view" onClick={() => setShownPreviewIndex(index)}>
            {renderPreview(attachment, isFilesVisible)}
            <Divider />
            <div className="media-download">
              {canUpdateIntervention && !!onToggleVisibility && attachment.question_result_id === undefined && (
                <div className="media-wrapper" onClick={evt => handleUpdateVisibility(evt, !attachment.visible_to_customer, attachment.id)}>
                  <Icon className={attachment.visible_to_customer ? "eye green" : "eye slash red"} />
                </div>
              )}
              {canPreviewFiles && (
                <div className="media-wrapper">
                  <a href={getGcsDownloadURL(attachment.url)} onClick={e => e.stopPropagation()} target="_blank" rel="noreferrer">
                    <Icon className="download blue" />
                  </a>
                </div>
              )}
              {onDeleteRequest && canDelete && Boolean(attachment.id) && (
                <div className="media-wrapper" onClick={e => handleDeleteAttachment(e, attachment, index)}>
                  <Icon className="trash grey" />
                </div>
              )}
            </div>
          </div>
        );
      })}
      <FilePreview files={attachments} previewIndex={shownPreviewIndex} setPreviewIndex={setShownPreviewIndex} />
    </div>
  );
};
